// frontend/src/pages/BillingPage.jsx
/**
 * BillingPage — subscription management hub.
 *
 * Sections:
 *   1. Current Plan card  — plan name badge, status badge, trial banner,
 *                           renewal date, past-due warning
 *   2. Plan Comparison    — monthly/annual toggle (P7-G11b), 4 plan cards
 *                           with feature comparison, Upgrade buttons
 *   3. Manage Billing     — Stripe Billing Portal button (owner only,
 *                           visible when a subscription exists)
 *   4. Invoices           — table of recent invoices with download links
 *   5. Referral card      — "Earn a free month — refer a team" (P7-G11c):
 *                           copy-to-clipboard link + stats + social share
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import toast from "react-hot-toast";

import { useAuth } from "../context/AuthContext";
import {
  createCheckout,
  createPortal,
  getBillingStatus,
  getInvoices,
  getReferralStats,
  getVoucherPreview,
  redeemVoucher,
} from "../api/billing";
import ErrorBanner from "../components/ErrorBanner";
import Spinner from "../components/Spinner";
import UsageWidget from "../components/UsageWidget";
import NextTierUnlock from "../components/NextTierUnlock";

// ─── Plan catalogue ────────────────────────────────────────────────────────────
// Monthly prices are in euros (QRFlow sells in EUR only). Annual prices are the yearly total (10 months
// equivalent for paid plans → exactly "2 months free"). Annual monthly-equivalent
// is the per-month cost when billed annually.
const PLANS = [
  {
    name: "Free",
    monthly: 0,
    annual: 0,
    annualMonthly: 0,
    popular: false,
    features: [
      { label: "QR codes", value: "5" },
      { label: "Scans / month", value: "500" },
      { label: "Team members", value: "1" },
      { label: "Analytics retention", value: "30 days" },
      { label: "API access", value: false },
      { label: "White label", value: false },
    ],
  },
  {
    name: "Starter",
    monthly: 19,
    annual: 190,
    annualMonthly: 15.83,
    popular: false,
    features: [
      { label: "QR codes", value: "50" },
      { label: "Scans / month", value: "5,000" },
      { label: "Team members", value: "3" },
      { label: "Analytics retention", value: "90 days" },
      { label: "API access", value: false },
      { label: "White label", value: false },
    ],
  },
  {
    name: "Pro",
    monthly: 59,
    annual: 590,
    annualMonthly: 49.17,
    popular: true,
    features: [
      { label: "QR codes", value: "500" },
      { label: "Scans / month", value: "50,000" },
      { label: "Team members", value: "10" },
      { label: "Analytics retention", value: "1 year" },
      { label: "API access", value: true },
      { label: "White label", value: false },
    ],
  },
  {
    name: "Business",
    monthly: 149,
    annual: 1490,
    annualMonthly: 124.17,
    popular: false,
    features: [
      { label: "QR codes", value: "5,000" },
      { label: "Scans / month", value: "500,000" },
      { label: "Team members", value: "50" },
      { label: "Analytics retention", value: "2 years" },
      { label: "API access", value: true },
      { label: "White label", value: true },
    ],
  },
];

// ─── Status helpers ────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  none:     { label: "No subscription",  cls: "bg-gray-100 text-gray-600" },
  trialing: { label: "Free trial",       cls: "bg-blue-50 text-blue-700" },
  active:   { label: "Active",           cls: "bg-green-50 text-green-700" },
  past_due: { label: "Past due",         cls: "bg-amber-50 text-amber-700" },
  canceled: { label: "Canceled",         cls: "bg-red-50 text-red-600" },
  unpaid:   { label: "Unpaid",           cls: "bg-red-50 text-red-600" },
};

function statusConfig(status) {
  return STATUS_CONFIG[status] ?? STATUS_CONFIG.none;
}

function formatDate(isoString) {
  if (!isoString) return null;
  return new Date(isoString).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function daysUntil(isoString) {
  if (!isoString) return null;
  const ms = new Date(isoString) - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function formatCents(cents, currency = "eur") {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
  }).format(cents / 100);
}

/**
 * Resolve a plan's displayed price, preferring live data from
 * /api/public/pricing over the static PLANS catalogue. Falls back to the
 * static values when livePricing hasn't loaded (or failed to load) so the
 * page never renders blank prices.
 */
function resolvePlanPrice(plan, livePricing, billingPeriod) {
  const entry = livePricing?.[plan.name.toLowerCase()];
  const monthlyPoint = entry?.monthly;
  const annualPoint = entry?.annual;

  const monthlyPrice =
    monthlyPoint?.cents != null ? monthlyPoint.cents / 100 : plan.monthly;
  const annualTotal =
    annualPoint?.cents != null ? annualPoint.cents / 100 : plan.annual;
  const displayPrice = billingPeriod === "annual" ? annualTotal / 12 : monthlyPrice;

  return { displayPrice, monthlyPrice, annualTotal };
}

// ─── Sub-components ────────────────────────────────────────────────────────────

/**
 * TrialBanner — shown at the top when the subscription is in trial.
 */
function TrialBanner({ trialEnd }) {
  const days = daysUntil(trialEnd);
  if (days === null) return null;
  return (
    <div
      role="status"
      className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-5 py-4"
    >
      <svg className="h-5 w-5 shrink-0 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span className="text-sm text-blue-800">
        <strong>Free trial active</strong> — {days === 0 ? "expires today" : `${days} day${days === 1 ? "" : "s"} remaining`}.
        {" "}Pick a plan below to keep your access after the trial ends.
      </span>
    </div>
  );
}
TrialBanner.propTypes = { trialEnd: PropTypes.string };

/**
 * PastDueBanner — prominent warning when payment has failed.
 */
function PastDueBanner() {
  return (
    <div
      role="alert"
      className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4"
    >
      <svg className="h-5 w-5 shrink-0 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
      <span className="text-sm text-amber-800">
        <strong>Payment failed.</strong> Please update your payment method to restore full access.
        Use the "Manage Subscription" button below.
      </span>
    </div>
  );
}

/**
 * CurrentPlanCard — shows plan name, subscription status, and renewal date.
 */
function CurrentPlanCard({ billingStatus }) {
  if (!billingStatus) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
        <div className="h-5 w-48 bg-gray-200 rounded" />
        <div className="mt-3 h-4 w-32 bg-gray-100 rounded" />
      </div>
    );
  }

  const {
    plan_name,
    status,
    current_period_end,
    trial_end,
    upcoming_price_cents,
    upcoming_price_date,
  } = billingStatus;
  const cfg = statusConfig(status);
  const renewalDate = formatDate(current_period_end);
  const upcomingDate = formatDate(upcoming_price_date);
  const upcomingPrice =
    upcoming_price_cents != null
      ? (upcoming_price_cents / 100).toLocaleString("en-US", {
          style: "currency",
          currency: "EUR",
        })
      : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-gray-900">Current Plan</h2>
        <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-0.5 text-sm font-medium text-gray-800">
          {plan_name}
        </span>
        <span
          data-testid="status-badge"
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.cls}`}
        >
          {cfg.label}
        </span>
      </div>

      {status === "trialing" && trial_end && (
        <p className="text-sm text-gray-500">
          Trial ends on <strong>{formatDate(trial_end)}</strong>
        </p>
      )}
      {renewalDate && status === "active" && (
        <p className="text-sm text-gray-500">
          Renews on <strong>{renewalDate}</strong>
        </p>
      )}
      {renewalDate && status === "past_due" && (
        <p className="text-sm text-gray-500">
          Period ends on <strong>{renewalDate}</strong>
        </p>
      )}

      {upcomingPrice && upcomingDate && (
        <div
          data-testid="upcoming-price-banner"
          className="rounded-lg bg-indigo-50 border border-indigo-200 px-3 py-2 text-sm text-indigo-800"
        >
          Starting <strong>{upcomingDate}</strong>, your plan price will be{" "}
          <strong>{upcomingPrice}/mo</strong>. The new price applies from your
          next renewal on or after that date.
        </div>
      )}
    </div>
  );
}
CurrentPlanCard.propTypes = {
  billingStatus: PropTypes.shape({
    plan_name: PropTypes.string,
    status: PropTypes.string,
    current_period_end: PropTypes.string,
    trial_end: PropTypes.string,
    upcoming_price_cents: PropTypes.number,
    upcoming_price_date: PropTypes.string,
  }),
};

/**
 * BillingPeriodToggle — animated pill toggle between Monthly and Annual billing.
 */
function BillingPeriodToggle({ value, onChange }) {
  return (
    <div
      role="group"
      aria-label="Billing period"
      className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 p-1"
    >
      <button
        type="button"
        role="radio"
        aria-checked={value === "monthly"}
        onClick={() => onChange("monthly")}
        className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
          value === "monthly"
            ? "bg-white shadow text-gray-900"
            : "text-gray-500 hover:text-gray-700"
        }`}
      >
        Monthly
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={value === "annual"}
        onClick={() => onChange("annual")}
        className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
          value === "annual"
            ? "bg-white shadow text-gray-900"
            : "text-gray-500 hover:text-gray-700"
        }`}
      >
        Annual
        <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-xs font-semibold text-green-700">
          2 months free
        </span>
      </button>
    </div>
  );
}
BillingPeriodToggle.propTypes = {
  value: PropTypes.oneOf(["monthly", "annual"]).isRequired,
  onChange: PropTypes.func.isRequired,
};

/**
 * FeatureLine — one row inside a plan card.
 */
function FeatureLine({ label, value }) {
  const isBoolean = typeof value === "boolean";
  return (
    <li className="flex items-center gap-2 text-sm text-gray-600">
      {isBoolean ? (
        value ? (
          <svg className="h-4 w-4 shrink-0 text-green-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="h-4 w-4 shrink-0 text-gray-300" viewBox="0 0 20 20" fill="currentColor">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
          </svg>
        )
      ) : (
        <svg className="h-4 w-4 shrink-0 text-green-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
        </svg>
      )}
      <span>
        <span className={isBoolean && !value ? "text-gray-400" : ""}>{label}</span>
        {!isBoolean && (
          <span className="ml-1 font-medium text-gray-900">{value}</span>
        )}
      </span>
    </li>
  );
}
FeatureLine.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
};

/**
 * PlanCard — a single plan column in the comparison grid.
 */
function PlanCard({ plan, billingPeriod, currentPlanName, onUpgrade, loadingPlan, isOwner, livePricing }) {
  const isCurrent = plan.name.toLowerCase() === currentPlanName?.toLowerCase();
  const isFree = plan.monthly === 0;

  const { displayPrice, monthlyPrice, annualTotal } = resolvePlanPrice(plan, livePricing, billingPeriod);

  let btnLabel = "Upgrade";
  if (isCurrent) btnLabel = "Current plan";
  else if (isFree) btnLabel = "Downgrade to Free";

  const btnDisabled = isCurrent || !isOwner || !!loadingPlan;
  const isLoading = loadingPlan === plan.name;

  return (
    <div
      data-testid={`plan-card-${plan.name.toLowerCase()}`}
      className={`relative flex flex-col rounded-xl border p-6 ${
        plan.popular
          ? "border-blue-500 shadow-md ring-1 ring-blue-500"
          : isCurrent
          ? "border-gray-300 bg-gray-50"
          : "border-gray-200 bg-white"
      }`}
    >
      {plan.popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 px-3 py-0.5 text-xs font-semibold text-white">
          Most popular
        </span>
      )}

      {/* Name */}
      <h3 className="text-base font-semibold text-gray-900">{plan.name}</h3>

      {/* Price */}
      <div className="mt-3 space-y-0.5">
        {!isFree && billingPeriod === "annual" && (
          <div className="flex items-baseline gap-1">
            <span className="text-sm text-gray-400 line-through">
              €{monthlyPrice}/mo
            </span>
            <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-xs font-semibold text-green-700">
              Save 2 months
            </span>
          </div>
        )}
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold text-gray-900">
            {isFree ? "Free" : `€${displayPrice.toFixed(0)}`}
          </span>
          {!isFree && (
            <span className="text-sm text-gray-500">/ mo</span>
          )}
        </div>
        {!isFree && billingPeriod === "annual" && (
          <p className="text-xs text-gray-400">
            Billed €{annualTotal}/year
          </p>
        )}
      </div>

      {/* Features */}
      <ul className="mt-5 flex-1 space-y-2">
        {plan.features.map((f) => (
          <FeatureLine key={f.label} label={f.label} value={f.value} />
        ))}
      </ul>

      {/* CTA */}
      <button
        type="button"
        onClick={() => onUpgrade(plan.name, billingPeriod)}
        disabled={btnDisabled}
        className={`mt-6 w-full rounded-lg py-2.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          isCurrent
            ? "cursor-default bg-gray-100 text-gray-400"
            : plan.popular
            ? "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        }`}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Redirecting…
          </span>
        ) : btnLabel}
      </button>
      {!isOwner && !isCurrent && (
        <p className="mt-2 text-center text-xs text-gray-400">Owner only</p>
      )}
    </div>
  );
}
PlanCard.propTypes = {
  plan: PropTypes.shape({
    name: PropTypes.string.isRequired,
    monthly: PropTypes.number.isRequired,
    annual: PropTypes.number.isRequired,
    annualMonthly: PropTypes.number.isRequired,
    popular: PropTypes.bool.isRequired,
    features: PropTypes.array.isRequired,
  }).isRequired,
  billingPeriod: PropTypes.oneOf(["monthly", "annual"]).isRequired,
  currentPlanName: PropTypes.string,
  onUpgrade: PropTypes.func.isRequired,
  loadingPlan: PropTypes.string,
  isOwner: PropTypes.bool.isRequired,
  livePricing: PropTypes.object,
};

/**
 * InvoiceTable — list of recent invoices from Stripe.
 */
function InvoiceTable({ invoices, loading, error }) {
  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-10 rounded bg-gray-100" />
        ))}
      </div>
    );
  }
  if (error) {
    return <p className="text-sm text-gray-500">Invoices unavailable right now.</p>;
  }
  if (!invoices.length) {
    return (
      <p data-testid="invoices-empty" className="text-sm text-gray-500">
        No invoices yet.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto -mx-6">
      <table className="min-w-full" data-testid="invoices-table">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              Status
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
              Invoice
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {invoices.map((inv) => (
            <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-3 text-sm text-gray-700">
                {new Date(inv.created * 1000).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </td>
              <td className="px-6 py-3 text-sm font-medium text-gray-900">
                {formatCents(inv.amount_due)}
              </td>
              <td className="px-6 py-3">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                    inv.status === "paid"
                      ? "bg-green-50 text-green-700"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {inv.status ?? "—"}
                </span>
              </td>
              <td className="px-6 py-3 text-right">
                {inv.hosted_invoice_url ? (
                  <a
                    href={inv.hosted_invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    View
                  </a>
                ) : (
                  <span className="text-sm text-gray-300">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
InvoiceTable.propTypes = {
  invoices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      amount_due: PropTypes.number.isRequired,
      status: PropTypes.string,
      created: PropTypes.number.isRequired,
      hosted_invoice_url: PropTypes.string,
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
};

/**
 * ReferralCard — "Earn a free month — refer a team."
 */
function ReferralCard() {
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);

  useEffect(() => {
    let active = true;
    getReferralStats()
      .then((d) => active && setStats(d))
      .catch(() => active && setStats(null));
    return () => {
      active = false;
      clearTimeout(copiedTimer.current);
    };
  }, []);

  const handleCopy = useCallback(() => {
    if (!stats?.referral_link) return;
    navigator.clipboard.writeText(stats.referral_link).then(() => {
      setCopied(true);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2000);
    });
  }, [stats]);

  const twitterUrl = stats
    ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        "Try QRFlow — the QR code platform for teams. Use my link to get started:"
      )}&url=${encodeURIComponent(stats.referral_link)}`
    : "#";

  const linkedinUrl = stats
    ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(stats.referral_link)}`
    : "#";

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-50">
          <svg className="h-5 w-5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Earn a free month — refer a team
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            Share your link. When someone subscribes through it, you both win:
            they get a great QR platform and you get 1 month free.
          </p>
        </div>
      </div>

      {/* Referral link */}
      {stats ? (
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={stats.referral_link}
            aria-label="Your referral link"
            className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            onFocus={(e) => e.target.select()}
          />
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Copy referral link"
            className="shrink-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {copied ? (
              <span className="flex items-center gap-1 text-green-600">
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                </svg>
                Copied!
              </span>
            ) : (
              "Copy"
            )}
          </button>
        </div>
      ) : (
        <div className="h-10 animate-pulse rounded-lg bg-gray-100" />
      )}

      {/* Stats + Share */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Stats */}
        {stats && (
          <div className="flex items-center gap-5 text-sm">
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900">{stats.total_referred}</p>
              <p className="text-xs text-gray-500">Referred</p>
            </div>
            <div className="h-8 w-px bg-gray-200" />
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900">{stats.converted}</p>
              <p className="text-xs text-gray-500">Converted</p>
            </div>
            <div className="h-8 w-px bg-gray-200" />
            <div className="text-center">
              <p className="text-lg font-bold text-green-600">{stats.rewards_earned}</p>
              <p className="text-xs text-gray-500">Months earned</p>
            </div>
          </div>
        )}

        {/* Share buttons */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Share:</span>
          <a
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Twitter / X"
            className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-800 transition-colors"
          >
            {/* X (Twitter) icon */}
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
          <a
            href={linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on LinkedIn"
            className="rounded-lg border border-gray-200 bg-white p-2 text-blue-600 hover:bg-blue-50 transition-colors"
          >
            {/* LinkedIn icon */}
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * VoucherSection — apply a discount / comp code.
 *
 * Lives alongside the plan-comparison grid so it's visible during the upgrade
 * flow. Typing a code triggers a 500ms-debounced preview call; a valid code
 * shows a green "You save X" badge, an invalid one a red error. The Apply
 * button (owner only) redeems the code and refreshes billing status.
 */
function VoucherSection({ isOwner, onApplied }) {
  const [code, setCode] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(false);
  const [applying, setApplying] = useState(false);
  const debounceTimer = useRef(null);

  // Debounced preview lookup whenever the code changes.
  useEffect(() => {
    clearTimeout(debounceTimer.current);
    const trimmed = code.trim();
    setError(null);
    setPreview(null);
    if (!trimmed) {
      setChecking(false);
      return undefined;
    }
    let cancelled = false;
    setChecking(true);
    debounceTimer.current = setTimeout(() => {
      getVoucherPreview(trimmed)
        .then((data) => {
          if (cancelled) return;
          setPreview(data);
          setError(null);
        })
        .catch(() => {
          if (cancelled) return;
          setPreview(null);
          setError("That voucher code is not valid.");
        })
        .finally(() => !cancelled && setChecking(false));
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(debounceTimer.current);
    };
  }, [code]);

  const handleApply = useCallback(async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setApplying(true);
    try {
      const result = await redeemVoucher(trimmed, null);
      toast.success(`Voucher applied — ${result.description}.`, { duration: 5000 });
      setCode("");
      setPreview(null);
      setError(null);
      onApplied?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not apply this voucher.";
      toast.error(msg);
    } finally {
      setApplying(false);
    }
  }, [code, onApplied]);

  const canApply = isOwner && !!preview && !applying;

  return (
    <section
      aria-labelledby="voucher-heading"
      className="bg-white rounded-xl border border-gray-200 p-6 space-y-3"
    >
      <h2 id="voucher-heading" className="text-base font-semibold text-gray-900">
        Have a voucher?
      </h2>
      <p className="text-sm text-gray-500">
        Enter a discount code to preview your savings, then apply it to your account.
      </p>

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          aria-label="Voucher code"
          placeholder="e.g. LAUNCH50"
          autoComplete="off"
          className="flex-1 min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm uppercase tracking-wide text-gray-900 placeholder:normal-case placeholder:tracking-normal focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={handleApply}
          disabled={!canApply}
          data-testid="apply-voucher-btn"
          className="shrink-0 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          {applying ? "Applying…" : "Apply"}
        </button>
      </div>

      {checking && (
        <p className="text-xs text-gray-400">Checking code…</p>
      )}

      {preview && (
        <div
          data-testid="voucher-preview"
          role="status"
          className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800"
        >
          <svg className="h-4 w-4 shrink-0 text-green-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
          </svg>
          <span>
            You save <strong>{preview.description}</strong>
          </span>
        </div>
      )}

      {error && (
        <div
          data-testid="voucher-error"
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      {!isOwner && (
        <p className="text-xs text-gray-400">Only the org owner can apply a voucher.</p>
      )}
    </section>
  );
}
VoucherSection.propTypes = {
  isOwner: PropTypes.bool.isRequired,
  onApplied: PropTypes.func,
};

// ─── Main page ─────────────────────────────────────────────────────────────────

/**
 * BillingPage — the full billing management hub.
 *
 * Route: /billing
 * Required role: any member can view; owner-only actions (checkout, portal) are
 * gated at the button level (disabled with a tooltip for non-owners).
 */
function BillingPage() {
  const { currentOrg } = useAuth();
  const [searchParams] = useSearchParams();

  // ── Data state ──
  const [billingStatus, setBillingStatus] = useState(null);
  const [statusError, setStatusError] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState(false);

  // ── UI state ──
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [checkingOutPlan, setCheckingOutPlan] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  // Live plan prices from /api/public/pricing (EUR — the only currency
  // QRFlow sells in), keyed by lowercased plan name. null until loaded or on
  // failure, in which case PLANS' static values are shown (see
  // resolvePlanPrice) — mirrors PricingPage.jsx's `live` state so both
  // surfaces can never disagree about the current price.
  const [livePricing, setLivePricing] = useState(null);

  useEffect(() => {
    if (typeof fetch !== "function") return;
    fetch("/api/public/pricing")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.plans) {
          const map = {};
          for (const p of d.plans) map[p.plan_name.toLowerCase()] = p;
          setLivePricing(map);
        }
      })
      .catch(() => {
        /* keep static PLANS prices on any failure */
      });
  }, []);

  const isOwner = currentOrg?.role === "owner";
  const hasSubscription = billingStatus?.status &&
    ["trialing", "active", "past_due", "unpaid"].includes(billingStatus.status);

  // ── Fetch on mount ──
  useEffect(() => {
    let active = true;

    getBillingStatus()
      .then((d) => active && setBillingStatus(d))
      .catch(() => active && setStatusError(true));

    getInvoices()
      .then((d) => active && setInvoices(d))
      .catch(() => active && setInvoicesError(true))
      .finally(() => active && setInvoicesLoading(false));

    return () => { active = false; };
  }, []);

  // ── Handle ?checkout=success / ?checkout=cancel after Stripe redirect ──
  useEffect(() => {
    const outcome = searchParams.get("checkout");
    if (outcome === "success") {
      toast.success("Subscription activated! Your plan has been updated.", { duration: 5000 });
      // Refresh billing status to reflect the updated plan.
      getBillingStatus()
        .then(setBillingStatus)
        .catch(() => {});
    } else if (outcome === "cancel") {
      toast("Checkout was cancelled. No charge was made.", { icon: "ℹ️" });
    }
  }, []); // intentionally run once on mount only

  // ── Checkout ──
  const handleUpgrade = useCallback(async (planName, period) => {
    setCheckingOutPlan(planName);
    try {
      const url = await createCheckout(planName, period);
      window.location.href = url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not start checkout. Please try again.";
      toast.error(msg);
      setCheckingOutPlan(null);
    }
  }, []);

  // ── Refresh billing status (used after a voucher is applied) ──
  const refreshBillingStatus = useCallback(() => {
    getBillingStatus()
      .then(setBillingStatus)
      .catch(() => {});
  }, []);

  // ── Portal ──
  const handlePortal = useCallback(async () => {
    setOpeningPortal(true);
    try {
      const url = await createPortal();
      window.location.href = url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not open billing portal. Please try again.";
      toast.error(msg);
      setOpeningPortal(false);
    }
  }, []);

  if (statusError) {
    return (
      <div className="max-w-4xl mx-auto">
        <ErrorBanner message="Billing information could not be loaded. Please try refreshing." />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <h1 className="text-xl font-bold text-gray-900">Billing</h1>

      {/* Trial banner */}
      {billingStatus?.status === "trialing" && billingStatus.trial_end && (
        <TrialBanner trialEnd={billingStatus.trial_end} />
      )}

      {/* Past-due warning */}
      {billingStatus?.status === "past_due" && <PastDueBanner />}

      {/* ── Section 1: Current Plan + live usage ── */}
      <CurrentPlanCard billingStatus={billingStatus} />
      <UsageWidget />

      {/* ── What you'll unlock on the next tier (pricing.js-driven) ── */}
      <NextTierUnlock
        onUpgrade={(name) => handleUpgrade(name, billingPeriod)}
        isOwner={isOwner}
      />

      {/* ── Section 2: Plan Comparison ── */}
      <section aria-labelledby="plans-heading" className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 id="plans-heading" className="text-lg font-semibold text-gray-900">
            Plans
          </h2>
          <BillingPeriodToggle value={billingPeriod} onChange={setBillingPeriod} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLANS.map((plan) => (
            <PlanCard
              key={plan.name}
              plan={plan}
              billingPeriod={billingPeriod}
              currentPlanName={billingStatus?.plan_name}
              onUpgrade={handleUpgrade}
              loadingPlan={checkingOutPlan}
              isOwner={isOwner}
              livePricing={livePricing}
            />
          ))}
        </div>
      </section>

      {/* ── Voucher ── (visible during plan selection / upgrade flow) */}
      <VoucherSection isOwner={isOwner} onApplied={refreshBillingStatus} />

      {/* ── Section 3: Manage Billing ── */}
      {(hasSubscription || isOwner) && (
        <section
          aria-labelledby="manage-heading"
          className="bg-white rounded-xl border border-gray-200 p-6 space-y-3"
        >
          <h2 id="manage-heading" className="text-base font-semibold text-gray-900">
            Manage Subscription
          </h2>
          <p className="text-sm text-gray-500">
            Update your payment method, view receipts, or change your subscription
            in the Stripe Billing Portal.
          </p>
          <button
            type="button"
            onClick={handlePortal}
            disabled={!isOwner || openingPortal}
            data-testid="manage-billing-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {openingPortal ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Opening…
              </>
            ) : (
              <>
                Manage Subscription
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </>
            )}
          </button>
          {!isOwner && (
            <p className="text-xs text-gray-400">Only the org owner can manage the subscription.</p>
          )}
        </section>
      )}

      {/* ── Section 4: Invoices ── */}
      <section
        aria-labelledby="invoices-heading"
        className="bg-white rounded-xl border border-gray-200 p-6 space-y-4"
      >
        <h2 id="invoices-heading" className="text-base font-semibold text-gray-900">
          Invoices
        </h2>
        <InvoiceTable
          invoices={invoices}
          loading={invoicesLoading}
          error={invoicesError}
        />
      </section>

      {/* ── Section 5: Referral card ── */}
      <ReferralCard />
    </div>
  );
}

export default BillingPage;
